import { useState, useEffect } from 'react'
import { Button } from './ui/button'
import { Menu, X } from 'lucide-react'
import logo from '/logo-400.webp' // Updated path to webp
import { useSmoothScroll } from '@/hooks/useSmoothScroll' // Corrected import path
import { useIsMobile } from '@/hooks/use-mobile'

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { handleNavClick } = useSmoothScroll()
  const isMobile = useIsMobile()

  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50)
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { name: 'Hjem', targetId: 'home' },
    { name: 'Tjenester', targetId: 'tjenester' },
    { name: 'Priser', targetId: 'priser' },
    { name: 'Arbeid', targetId: 'arbeid' },
    { name: 'Om oss', targetId: 'about' },
  ]

  const handleMenuClick = (targetId: string) => {
    handleNavClick(targetId)
    setIsMobileMenuOpen(false)
  }

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled
        ? isMobile
          ? 'bg-background/95 border-b border-border/30'
          : 'bg-background/80 backdrop-blur-md border-b border-border/30'
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => handleMenuClick('home')}>
            <img
              src={logo}
              srcSet="/logo-200.webp 200w, /logo-400.webp 400w"
              sizes="(max-width: 768px) 200px, 400px"
              alt="Pyksel Logo"
              className="h-12 w-auto logo-glow"
              fetchPriority="high"
              width="180"
              height="48"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <span
                key={item.name}
                onClick={() => handleMenuClick(item.targetId)}
                className="text-foreground hover:text-primary transition-colors smooth-transition cursor-pointer"
              >
                {item.name}
              </span>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button onClick={() => handleMenuClick('contact')} className="hero-glow smooth-transition hover:scale-105">
              Kontakt
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Lukk meny' : 'Åpne meny'}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-background/95 border-b border-border/30">
            <div className="px-6 py-4 space-y-4">
              {navItems.map((item) => (
                <span
                  key={item.name}
                  onClick={() => handleMenuClick(item.targetId)}
                  className="block text-foreground hover:text-primary transition-colors cursor-pointer"
                >
                  {item.name}
                </span>
              ))}
              <Button asChild className="w-full mt-4">
                <span onClick={() => handleMenuClick('contact')} className="cursor-pointer">
                  Kontakt
                </span>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
